/* eslint-disable max-statements */
import { Button, Checkbox, Grid, Group, SegmentedControl, Stack, Text } from '@mantine/core';
import { useMemo } from 'react';

import { DocumentsEnum, TemplateType, TSDMasterConsignmentInputMode, TSDType } from '@e-origin/shared';

import { DateTimePickerInput, DocumentFormFactory, Select, TextInput, UploadFile } from '@components';
import { TSDDocumentFormSchema } from '@features/tsd/TSDView/TSDViewConsignment/TSDDocumentFormSchema';
import { shouldShowForTypes } from '@features/tsd/utils';
import { useCustomersNamesAndIds } from '@hooks/customer';
import { useDeclarant, useGoodLocation } from '@hooks/declarant';
import { useTemplates } from '@hooks/useTemplates';
import { DEFAULT_DATE_AND_TIME_OF_PRESENTATION_OF_THE_GOODS, DEFAULT_DECLARATION_DATE, useNewTSDFormInitializer } from './useNewTSDFormInitializer';

interface NewTSDProps {
  onHide(): void;
  afterCreate(): void;
  tsdView: TSDType;
}

export const NewTSD: React.FC<NewTSDProps> = ({ onHide, afterCreate, tsdView }) => {
  const customersNamesAndIds = useCustomersNamesAndIds();
  const { data: declarant } = useDeclarant();
  const { data: { data: goodsLocations } = {}, isLoading } = useGoodLocation(declarant._id);
  const templateType =
    tsdView === TSDType.COMBINED ? TemplateType.COMBINED_TSD : tsdView === TSDType.TRANSFERRED ? TemplateType.TRANSFERRED_TSD : TemplateType.PRELODGED_TSD;
  const { data: templates } = useTemplates({ filters: { type: templateType, deleted: false } });
  const documentFormSchema = TSDDocumentFormSchema();

  const templatesList = useMemo(
    () =>
      templates.map((template) => ({
        label: template.name,
        value: template._id,
      })),
    [templates],
  );

  const isCustomersNamesAndIdsEmpty = customersNamesAndIds.length === 0;

  const { form, onSubmit, submitting } = useNewTSDFormInitializer({ tsdView, onHide, afterCreate });

  const locationOfGoodsData = useMemo(
    () =>
      goodsLocations?.map((location) => ({
        label: location.unLocode || '',
        value: location.unLocode || '',
      })) || [],
    [goodsLocations],
  );

  const warehouseData = useMemo(() => {
    if (!form.values.locationOfGoodsUNLocode || !goodsLocations) {
      return [];
    }

    const selectedLocation = goodsLocations.find((loc) => loc.unLocode === form.values.locationOfGoodsUNLocode);

    return (
      selectedLocation?.warehouses?.map((warehouse) => ({
        label: `${warehouse.type} - ${warehouse.identifier}`,
        value: warehouse.identifier || '',
      })) || []
    );
  }, [form.values.locationOfGoodsUNLocode, goodsLocations]);

  const handleLocationChange = (value: string | null) => {
    form.setValues({
      locationOfGoodsUNLocode: value,
      warehouse: '',
    });
  };

  return (
    <form onSubmit={form.onSubmit(onSubmit)}>
      <Stack gap="sm" mb="xl">
        <TextInput {...form.getInputProps('name')} label="Temporary storage name" size="lg" data-pw="temporary-storage-name-input" />

        {!isCustomersNamesAndIdsEmpty && (
          <Select
            {...form.getInputProps('customer')}
            label="Select customer"
            data={customersNamesAndIds}
            searchable
            size="lg"
            maxDropdownHeight={200}
            data-pw="select-customer-dropdown"
          />
        )}

        <Select
          {...form.getInputProps('template')}
          label="Select template"
          data={templatesList}
          searchable
          size="lg"
          maxDropdownHeight={200}
          data-pw="select-template-dropdown"
        />

        {isCustomersNamesAndIdsEmpty && <Text c="#FF0000">You don't have any customers. Please, create a customer.</Text>}

        <Select
          {...form.getInputProps('locationOfGoodsUNLocode')}
          label="Location of Goods"
          data={locationOfGoodsData}
          searchable
          size="lg"
          disabled={isLoading}
          data-pw="location-of-goods-dropdown"
          onChange={handleLocationChange}
        />

        <Select
          key={form.values.locationOfGoodsUNLocode}
          {...form.getInputProps('warehouse')}
          label="Warehouse"
          data={warehouseData}
          searchable
          size="lg"
          disabled={!form.values.locationOfGoodsUNLocode || isLoading}
          data-pw="warehouse-dropdown"
        />
        {shouldShowForTypes(tsdView, [TSDType.COMBINED, TSDType.PRELODGED]) && (
          <Checkbox {...form.getInputProps('generalInfo.ensReUseIndicator')} label="ENS re-use" size="lg" data-pw="ens-reuse-indicator-checkbox" />
        )}

        {shouldShowForTypes(tsdView, [TSDType.COMBINED, TSDType.PRELODGED]) && (
          <Stack gap="sm" mt="sm">
            <SegmentedControl
              value={form.values.masterConsignmentInputMode}
              data-pw="master-consignment-input-mode"
              onChange={(value) => {
                form.setFieldValue('masterConsignmentInputMode', value as TSDMasterConsignmentInputMode);
              }}
              data={[
                { label: 'Upload goods items', value: TSDMasterConsignmentInputMode.UPLOAD },
                { label: 'Enter totals manually', value: TSDMasterConsignmentInputMode.MANUAL },
              ]}
              fullWidth
            />
            {form.values.masterConsignmentInputMode === TSDMasterConsignmentInputMode.UPLOAD && (
              <Stack gap="sm" mb="xl">
                <Stack gap={4}>
                  <Text fw={500} size="sm">
                    Goods items
                  </Text>

                  <Text size="xs" c="dimmed">
                    Upload an XLSX file. Total packages and gross mass will be calculated from the imported goods items.
                  </Text>

                  <UploadFile {...form.getInputProps('file')} isMulti={false} type="xlsx" p="xs" />
                </Stack>
              </Stack>
            )}
          </Stack>
        )}

        {form.values.masterConsignmentInputMode === TSDMasterConsignmentInputMode.MANUAL && (
          <Grid align="center" mb="xl">
            <Grid.Col span={6}>
              <TextInput
                {...form.getInputProps('consignment.master.totalNumberOfPackages')}
                label="Total number of packages"
                size="lg"
                data-pw="total-number-of-packages-input"
              />
            </Grid.Col>
            <Grid.Col span={6}>
              <TextInput {...form.getInputProps('consignment.master.totalGrossMass')} label="Total gross mass" size="lg" data-pw="total-gross-mass-input" />
            </Grid.Col>
          </Grid>
        )}

        {shouldShowForTypes(tsdView, [TSDType.TRANSFERRED]) && (
          <>
            <TextInput {...form.getInputProps('messageInfo.LRN')} label="LRN" size="lg" data-pw="lrn-input" />

            <TextInput {...form.getInputProps('messageInfo.MRN')} label="MRN" size="lg" data-pw="mrn-input" />

            <Stack>
              <Text>Transport document</Text>
              <DocumentFormFactory
                type={DocumentsEnum.TRANSPORT_DOCUMENTS}
                form={form}
                editDisabled={false}
                documentFormSchema={documentFormSchema}
                pathPrefix="consignment.master.transportDocuments.0"
              />
            </Stack>
          </>
        )}

        {shouldShowForTypes(tsdView, [TSDType.COMBINED]) && (
          <>
            <DateTimePickerInput
              {...form.getInputProps('generalInfo.declarationDate')}
              maxDate={DEFAULT_DECLARATION_DATE}
              label="Declaration date"
              size="lg"
              data-pw="declaration-date-input"
            />

            <DateTimePickerInput
              {...form.getInputProps('generalInfo.dateAndTimeOfPresentationOfTheGoods')}
              maxDate={DEFAULT_DATE_AND_TIME_OF_PRESENTATION_OF_THE_GOODS}
              label="Date and time of presentation of the goods"
              size="lg"
              data-pw="date-and-time-of-presentation-input"
            />
          </>
        )}
      </Stack>

      <Group justify="center">
        <Button type="button" onClick={onHide} disabled={submitting} size="lg" variant="transparent">
          Cancel
        </Button>

        <Button type="submit" loading={submitting} disabled={!form.isValid() || isCustomersNamesAndIdsEmpty} variant="filled" size="lg" data-pw="create-btn">
          Create
        </Button>
      </Group>
    </form>
  );
};
